import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { DoesNotExistAspect, ExistsAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { endOfDay, format, startOfDay } from 'date-fns';
import { html, nothing } from 'lit';
import { guard } from 'lit/directives/guard.js';
import { AnsweringEntity } from '../../entities/answering.js';
import { AnsweringDetailView } from './AnsweringDetailView.js';

export const AnsweringDashboard = stateful(() => {
  // Use useState for the selected conversation and filters
  const [selectedConversationID, setSelectedConversationID] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [expectedAnswerFilter, setExpectedAnswerFilter] = useState<'all' | 'with' | 'without'>('all');
  const [channelTypeFilter, setChannelTypeFilter] = useState<string>('all');
  const [checkedByHumanFilter, setCheckedByHumanFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [ratedFilter, setRatedFilter] = useState<'all' | 'rated' | 'unrated'>('all');

  // Function to handle row click
  const handleRowClick = (conversation: AnsweringEntity) => {
    setSelectedConversationID(conversation._id);
  };

  // Function to close the detail view
  const closeDetailView = () => {
    setSelectedConversationID(null);
  };

  // Function to clear all filters
  const clearFilters = () => {
    setSearchText('');
    setStartDate('');
    setEndDate('');
    setExpectedAnswerFilter('all');
    setChannelTypeFilter('all');
    setCheckedByHumanFilter('all');
    setRatedFilter('all');
  };

  const conversationDataviewOptions = useMemo(() => {
    const filterAspects: any[] = [];

    // Add date range filter - if both dates are provided, use range mode
    if (startDate && endDate) {
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          secondValue: endOfDay(new Date(endDate)),
          mode: 'range',
        }),
      );
    } else if (startDate) {
      // Only start date - use after mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          mode: 'after',
        }),
      );
    } else if (endDate) {
      // Only end date - use before mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          secondValue: endOfDay(new Date(endDate)),
          mode: 'before',
        }),
      );
    }

    // Add expectedAnswer filter
    if (expectedAnswerFilter === 'with') {
      filterAspects.push(
        new StringAspect({
          fieldName: 'expectedAnswer',
          mode: 'regex',
          value: '.+',
        }),
      );
    } else if (expectedAnswerFilter === 'without') {
      filterAspects.push(
        new StringAspect({
          fieldName: 'expectedAnswer',
          mode: 'empty',
        }),
      );
    }

    // Add answeringChannelType filter
    if (channelTypeFilter !== 'all') {
      filterAspects.push(
        new StringAspect({
          fieldName: 'answeringChannelType',
          mode: 'equals',
          value: channelTypeFilter,
        }),
      );
    }

    // Add checkedByHuman filter
    if (checkedByHumanFilter === 'yes') {
      filterAspects.push(
        new BooleanAspect({
          fieldName: 'checkedByHuman',
          value: true,
        }),
      );
    } else if (checkedByHumanFilter === 'no') {
      // For "not checked", we want to include both false and null values
      // Use StringAspect with empty mode to catch all non-true values (null, false, undefined)
      filterAspects.push(
        new StringAspect({
          fieldName: 'checkedByHuman',
          mode: 'empty',
        }),
      );
    }

    // Add rating filter
    if (ratedFilter === 'rated') {
      filterAspects.push(
        new ExistsAspect({
          fieldName: 'rating',
        }),
      );
    } else if (ratedFilter === 'unrated') {
      filterAspects.push(
        new DoesNotExistAspect({
          fieldName: 'rating',
        }),
      );
    }

    return [
      buildFullOptions<typeof AnsweringEntity>({
        class: AnsweringEntity,
        isSortable: true,
        customColumns: [
          /*{
            fieldName: '_id',
            columnName: 'ID',
            columnWidth: '240px',
            renderItem: value => html` ${value._id || 'N/A'} `,
          }, */
          //{
          //  fieldName: 'body',
          //  columnName: 'Body Preview',
          //  renderItem: value => {
          //    const text = (value.body || '').replace(/<[^>]+>/g, '');
          //    const preview = text.length > 100 ? text.slice(0, 100) + '...' : text;
          //    return html`<div class="whitespace-pre-wrap">${preview.trim()}</div>`;
          //  },
          //},
          // {
          //   fieldName: 'environment',
          //   columnName: 'Environment',
          //   columnWidth: '120px',
          //   renderItem: value => html`
          //     <span
          //       class="badge ${value.environment === ConversationEnvironment.PRODUCTION
          //         ? 'badge-primary'
          //         : 'badge-secondary'}"
          //       >${value.environment || 'unknown'}</span
          //     >
          //   `,
          // },
          {
            fieldName: 'answeringChannelType',
            columnName: 'Channel',
            columnWidth: '200px',
            renderItem: value => html` ${value.answeringChannelType || 'N/A'} `,
          },
          {
            fieldName: 'foreignKey',
            columnName: 'Channel Ticket Key',
            columnWidth: '120px',
            renderItem: value => html` ${value.foreignKey || 'N/A'} `,
          },
          {
            fieldName: 'conversationPartId',
            columnName: 'ConversationPartId',
            columnWidth: '120px',
            renderItem: value => html` ${value.conversationPartId || 'N/A'} `,
          },
          {
            fieldName: 'expectedAnswer',
            columnName: 'Rateable',
            columnWidth: '80px',
            renderItem: value =>
              value.expectedAnswer
                ? html`<span class="text-secondary">✅</span>`
                : html`<span class="text-error">❌</span>`,
          },
          {
            fieldName: 'rating',
            columnName: 'Rating',
            columnWidth: '70px',
            renderItem: value => html` ${value.rating ?? 'N/A'} `,
          },
          {
            fieldName: 'odooTags',
            columnName: 'Tags',
            // columnWidth: '200px',
            renderItem: value => value.odooTags?.join(', ') || 'N/A',
          },
          {
            fieldName: 'createdAt',
            columnName: 'Created At',
            columnWidth: '175px',
            renderItem: value => (value.createdAt ? format(value.createdAt, 'Pp') : 'N/A'),
          },
          {
            fieldName: 'checkedByHuman',
            columnName: 'Checked by Human',
            columnWidth: '120px',
            renderItem: value =>
              value.checkedByHuman
                ? html`<span class="text-secondary">✅</span>`
                : html`<span class="text-error">❌</span>`,
          },
          {
            customID: '_options',
            isSortable: false,
            columnName: '',
            columnWidth: '50px',
            renderItem: value => html`
              <button
                class="btn btn-ghost btn-sm btn-circle"
                @click=${() => handleRowClick(value)}
                title="View Details"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            `,
          },
        ],
        view: new View({
          filter: new AdornisFilter({
            searchFields: ['odooTicketID', 'foreignKey', 'conversationPartId', 'odooTags'],
            search: searchText,
            aspects: filterAspects,
          }),
        }),
      }),
    ];
  }, [searchText, startDate, endDate, expectedAnswerFilter, channelTypeFilter, checkedByHumanFilter, ratedFilter]);

  return html`
    <div class="flex gap-4 h-full">
      <div
        class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto ${selectedConversationID
          ? 'w-2/3'
          : 'w-full'}"
      >
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-4">
            <h2 class="card-title text-2xl font-bold text-secondary">Answers over all usecases</h2>

            <div class="flex items-center gap-2">
              <button class="btn btn-sm btn-outline btn-secondary" @click=${() => setShowFilters(!showFilters)}>
                ${showFilters ? 'Hide Filters' : 'Show Filters'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                </svg>
              </button>
            </div>
          </div>

          ${showFilters
            ? html`
                <div class="bg-base-200 p-4 rounded-lg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                    <!-- Text Search -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Text Search</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search in conversations..."
                        class="input input-bordered input-sm w-full"
                        .value=${searchText}
                        @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Date Range - Combined in one container to keep them together -->
                    <div class="form-control col-span-1 md:col-span-2">
                      <label class="label">
                        <span class="label-text font-medium">Date Range</span>
                      </label>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="Start date"
                            class="input input-bordered input-sm w-full"
                            .value=${startDate}
                            @input=${(e: InputEvent) => setStartDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="End date"
                            class="input input-bordered input-sm w-full"
                            .value=${endDate}
                            @input=${(e: InputEvent) => setEndDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Expected Answer Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Expected Answer</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${expectedAnswerFilter}
                        @change=${(e: InputEvent) =>
                          setExpectedAnswerFilter((e.target as HTMLSelectElement).value as 'all' | 'with' | 'without')}
                      >
                        <option value="all">All</option>
                        <option value="with">With Expected Answer</option>
                        <option value="without">Without Expected Answer</option>
                      </select>
                    </div>

                    <!-- Channel Type Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Channel Type</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${channelTypeFilter}
                        @change=${(e: InputEvent) => setChannelTypeFilter((e.target as HTMLSelectElement).value)}
                      >
                        <option value="all">All</option>
                        <option value="INTERCOM_CONVERSATION">Intercom</option>
                        <option value="MAIL">Mail</option>
                        <option value="INTEGRATION_TEST_EMAIL">Integration Test Email</option>
                        <option value="INTEGRATION_TEST_CHAT">Integration Test Chat</option>
                        <option value="DEMO">Demo</option>
                      </select>
                    </div>

                    <!-- Checked by Human Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Checked by Human</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${checkedByHumanFilter}
                        @change=${(e: InputEvent) =>
                          setCheckedByHumanFilter((e.target as HTMLSelectElement).value as 'all' | 'yes' | 'no')}
                      >
                        <option value="all">All</option>
                        <option value="yes">Checked</option>
                        <option value="no">Not Checked</option>
                      </select>
                    </div>

                    <!-- Rating Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Rating</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${ratedFilter}
                        @change=${(e: InputEvent) =>
                          setRatedFilter((e.target as HTMLSelectElement).value as 'all' | 'rated' | 'unrated')}
                      >
                        <option value="all">All</option>
                        <option value="rated">Rated</option>
                        <option value="unrated">Unrated</option>
                      </select>
                    </div>

                    <!-- Filter Actions -->
                    <div class="col-start-7 flex justify-end mt-4">
                      <button class="btn btn-sm btn-outline btn-error mr-2" @click=${clearFilters}>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              `
            : nothing}

          <div class="overflow-x-auto">
            ${guard([conversationDataviewOptions], () =>
              DataTable({
                options: conversationDataviewOptions.dataViewOptions,
              }),
            )}
          </div>
        </div>
      </div>

      <!-- Detail view panel -->
      ${selectedConversationID
        ? html`
            <div class="w-1/3">
              ${AnsweringDetailView({
                conversationID: selectedConversationID,
                onClose: closeDetailView,
              })}
            </div>
          `
        : nothing}
    </div>
  `;
});
